import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Avatar,
  Stack,
  Typography,
    Chip
} from "@mui/material";
import {
  Send as SendIcon,
  ArrowUpward as ArrowUpwardIcon,
  DoneAll,
} from "@mui/icons-material";

import { getImageV3 } from "../utils/images";
import { brand, gray } from "../getLPTheme";
import _ from "lodash";
import { CHAT_MESSAGE_TYPE } from "../utils/constants";
import { getHourMinutes, isDaySwitch, getDay, today } from "../utils/dateTime";

const MESSAGE_READ_PREFIX = "<--misc--> read ";

const Chat = ({ messages, addMessage, specialistId }) => {
  const inputRef = useRef(null);
  const [input, setInput] = useState("");
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);

  const observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          let curr = entry.target;
          if (curr.dataset.timeout) {
            return;
          }

          curr.dataset.timeout = setTimeout(() => {
            if (curr.dataset.message) {
              let message = JSON.parse(curr.dataset.message);

              if (!_.some(message.readBy || [], m => m?.specialist?.id == specialistId) &&
                  message?.specialist?.id !== specialistId
              ) {
                addMessage(message?.id, CHAT_MESSAGE_TYPE.READ);
                observer.unobserve(curr);
              }
            }
          }, 1000);
        } else {
          clearTimeout(entry.target.dataset.timeout);
          entry.target.dataset.timeout = null;
        }
      });
    },
    {
      root: containerRef.current,
      threshold: 1.0,
      trackVisibility: true,
      delay: 1000,
    },
  );

  const setItemRef = (message, index) => (el) => {
    if (!el) return;
    if (index === messages.length - 1) {
      bottomRef.current = el;
    }
    if (index === 0) {
      topRef.current = el;
    }

    if (_.some(message?.readBy, (it) => it?.specialist?.id === specialistId))
      return;

    el.dataset.message = JSON.stringify(message);
    observer.observe(el);
  };

  useEffect(() => {
    if (!containerRef) {
      return;
    }
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, []);

  const handleSend = () => {
    if (input.trim()) {
      addMessage(input, CHAT_MESSAGE_TYPE.COMMON);
      setInput("");
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      inputRef.current?.focus();
    }
  };

  const getAuthor = (message) => {
    if (message?.specialist) {
      return message?.specialist;
    }

    if (message?.client) {
      return message?.client;
    }

    if (message?.admin) {
      return message?.admin;
    }

      return null;
  };
;

  return (
    // <Paper elevation={0} sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
    <Box
      sx={{
        height: "100%",
        fontSize: "12px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
      }}
    >
      {/* Message List Area */}
      <Box
        sx={{ flexGrow: 1, overflowY: "auto", padding: 2, minHeight: 0 }}
        ref={containerRef}
      >
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            overflowAnchor: "none",
          }}
        >
          {messages.map((message, index, array) => (
              <>
                {
                  isDaySwitch(array[index-1]?.createdAt, message?.createdAt) ?
                     getDay(today()) === getDay(message?.createdAt) ?
                         <Chip label={"сегодня"}/>
                         : <Chip label={getDay(message?.createdAt)}/>
                    : null
                }
            <ListItem
              key={message.id}
              ref={setItemRef(message, index)}
              sx={{
                justifyContent:
                  message?.specialist?.id === specialistId
                    ? "flex-end"
                    : "flex-start",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  justifyContent:
                    message?.specialist?.id === specialistId
                      ? "flex-end"
                      : "flex-start",
                }}
              >
                {message?.specialist?.id !== specialistId ? (
                  <Box sx={{ width: "40px", height: "40px" }}>
                    {(index === array.length - 1 ||
                      getAuthor(array[index + 1])?.id !==
                        getAuthor(message)?.id) && (
                      <Avatar
                        alt={`${getAuthor(message)?.firstName} ${getAuthor(message)?.lastName}`}
                        src={getImageV3(
                          _.nth(
                            getAuthor(message)?.images?.map((im) => im?.id),
                            0,
                          ),
                        )}
                      >{_.head(getAuthor(message)?.firstName ? getAuthor(message)?.firstName : "")}</Avatar>
                    )}
                  </Box>
                ) : null}
                <Box
                  sx={{
                    padding: 1.5,
                    fontSize: "10px",
                    borderRadius: "20px",
                    bgcolor:
                      message?.specialist?.id === specialistId
                        ? brand[100]
                        : "grey.100",
                    color: "black",
                    maxWidth: "70%",
                  }}
                >
                  <ListItemText
                    primary={message.text}
                    secondary={
                      message?.specialist?.id !== specialistId ? (
                          (index === array.length - 1 ||
                            getAuthor(array[index + 1])?.id !==
                              getAuthor(message)?.id) && (
                          <Stack
                              direction={"row"}
                              spacing={1}
                              justifyContent={"flex-end"}
                          >
                            <Typography
                                sx={{ fontSize: "12px", color: "grey.400" }}
                            >
                              {`${getAuthor(message)?.firstName} ${getAuthor(message)?.lastName}`}
                            </Typography>
                            <Typography
                                sx={{ fontSize: "12px", color: "grey.400" }}
                            >
                              {getHourMinutes(message.createdAt)}
                            </Typography>
                          </Stack>
                          )
                      ) : (
                        <Stack
                          direction={"row"}
                          spacing={1}
                          justifyContent={"flex-end"}
                        >
                          <Typography
                            sx={{ fontSize: "12px", color: "grey.400" }}
                          >
                            {getHourMinutes(message.createdAt)}
                          </Typography>
                          {message?.readBy?.length && (
                            <DoneAll fontSize={"12px"} sx={{color: "grey.400"}} />
                          )}
                        </Stack>
                      )
                    }
                    primaryTypographyProps={{ component: "div" }}
                    secondaryTypographyProps={{ component: "div" }}
                    sx={{
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      whiteSpace: "pre-wrap",
                      m: 0,
                    }}
                  />
                </Box>
              </Stack>
            </ListItem>
            </>
          ))}
        </List>
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 1,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{ justifyContent: "center", alignItems: "flex-end" }}
        >
          {/*  occupies all the container */}
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              fullWidth
              multiline
              size="small"
              minRows={1}
              maxRows={4}
              inputRef={inputRef}
              variant="outlined"
              placeholder="Сообщение"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
          </Box>
          {/*  occupies only what is left the container */}
          <Box sx={{ flexShrink: 0 }}>
            <Button
              variant={"contained"}
              onMouseDown={(e) => e.preventDefault()}
            >
              <ArrowUpwardIcon
                fontSize={"medium"}
                sx={{ fontWeight: "300" }}
                onClick={handleSend}
              ></ArrowUpwardIcon>
            </Button>
          </Box>
          {/*<Button variant="contained" color="primary" onClick={handleSend}>*/}
          {/*    Send */}
          {/*</Button>*/}
        </Stack>
      </Box>
    </Box>
    // </Paper>
  );
};

export default Chat;
